import { useEffect, useRef, useState } from 'react';
import { useLibraryStore } from '@/state/libraryStore';
import { useAppStore } from '@/state/store';
import { fetchFileContent, fileDownloadUrl } from '@/lib/api/libraryApi';
import type { LibraryModel } from '@/types/library';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function BatchImportZone() {
  const runBatchImport = useLibraryStore((s) => s.runBatchImport);
  const batchItems = useLibraryStore((s) => s.batchItems);
  const isBatchRunning = useLibraryStore((s) => s.isBatchRunning);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    void runBatchImport(Array.from(fileList));
  }

  return (
    <div style={{ marginBottom: 28 }}>
      <h2 className="section-title">Bat Model Library</h2>
      <p className="section-subtitle">
        Drop files for as many models and sizes as you like, all at once — they're grouped automatically and each
        one is generated and saved on its own.
      </p>

      <div
        className={`dropzone${isDragging ? ' is-dragging' : ''}`}
        style={{ maxWidth: 520, padding: '20px 16px' }}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        role="button"
        tabIndex={0}
        aria-label="Batch import files for multiple models"
      >
        <div className="dropzone-title">{isBatchRunning ? 'Processing…' : 'Drop files for any number of models here'}</div>
        <div className="dropzone-subtitle">e.g. all 6 files for 10 different sizes at once — each is auto-detected and saved</div>
        <input ref={inputRef} type="file" multiple onChange={(e) => handleFiles(e.target.files)} />
      </div>

      {batchItems.length > 0 && (
        <div className="stat-list" style={{ maxWidth: 520, marginTop: 14 }}>
          {batchItems.map((item) => (
            <div className="stat-row" key={item.baseName}>
              <span className="stat-label">
                {item.baseName} <span style={{ color: 'var(--text-tertiary)' }}>({item.fileCount} files)</span>
              </span>
              <span
                className="stat-value"
                style={{
                  color:
                    item.status === 'saved'
                      ? 'var(--success)'
                      : item.status === 'error'
                        ? 'var(--danger)'
                        : 'var(--text-secondary)',
                }}
                title={item.message}
              >
                {item.status === 'pending' && 'Waiting…'}
                {item.status === 'processing' && 'Processing…'}
                {item.status === 'saved' && 'Saved ✓'}
                {item.status === 'error' && 'Failed'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SizeFilesList({ model }: { model: LibraryModel }) {
  const selectedSizeId = useLibraryStore((s) => s.selectedSizeId);
  const selectedSizeFiles = useLibraryStore((s) => s.selectedSizeFiles);
  const isLoadingFiles = useLibraryStore((s) => s.isLoadingFiles);
  const removeSize = useLibraryStore((s) => s.removeSize);
  const loadFileGroup = useAppStore((s) => s.loadFileGroup);
  const setActiveTab = useAppStore((s) => s.setActiveTab);
  const pushStatus = useAppStore((s) => s.pushStatus);
  const [isLoadingIntoStudio, setIsLoadingIntoStudio] = useState(false);

  const size = model.sizes.find((s) => s.id === selectedSizeId);
  if (!size || !selectedSizeFiles) return null;

  async function handleLoadIntoStudio() {
    if (!selectedSizeFiles || selectedSizeFiles.length === 0) return;
    setIsLoadingIntoStudio(true);
    try {
      const files = await Promise.all(
        selectedSizeFiles.map(async (f) => {
          const text = await fetchFileContent(f.id);
          return new File([text], f.file_name, { type: 'text/plain' });
        }),
      );
      await loadFileGroup(files);
      setActiveTab('viewer');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load this size into the Studio.';
      pushStatus('error', message);
    } finally {
      setIsLoadingIntoStudio(false);
    }
  }

  return (
    <div style={{ marginTop: 16, paddingLeft: 16, borderLeft: '2px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <strong style={{ fontSize: 13 }}>
          {model.name} — size {size.size_label}
        </strong>
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
          generated from .{size.source_format} · {size.profile_points ?? '—'} points ·{' '}
          {size.auto_trim_confidence !== null ? `${(size.auto_trim_confidence * 100).toFixed(0)}% confidence` : ''}
        </span>
      </div>

      {isLoadingFiles ? (
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Loading files…</div>
      ) : (
        <>
          <div className="stat-list" style={{ maxWidth: 480 }}>
            {selectedSizeFiles.map((f) => (
              <div className="stat-row" key={f.id}>
                <span className="stat-label">
                  .{f.file_extension} <span style={{ color: 'var(--text-tertiary)' }}>{f.file_name}</span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="stat-value" style={{ fontSize: 11 }}>
                    {formatBytes(f.size_bytes)}
                  </span>
                  <a
                    href={fileDownloadUrl(f.id)}
                    download={f.file_name}
                    style={{ fontSize: 11, color: 'var(--steel)' }}
                  >
                    Download
                  </a>
                </span>
              </div>
            ))}
          </div>

          <div className="btn-row" style={{ maxWidth: 480, marginTop: 10 }}>
            <button className="btn btn-primary" disabled={isLoadingIntoStudio} onClick={handleLoadIntoStudio}>
              {isLoadingIntoStudio ? 'Loading…' : 'Load into Studio'}
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => {
                if (confirm(`Remove ${model.name} size ${size.size_label} and its files from the library?`)) {
                  void removeSize(model.id, size.id);
                }
              }}
            >
              Remove
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function ModelRow({ model }: { model: LibraryModel }) {
  const selectedModelId = useLibraryStore((s) => s.selectedModelId);
  const selectedSizeId = useLibraryStore((s) => s.selectedSizeId);
  const selectModel = useLibraryStore((s) => s.selectModel);
  const selectSize = useLibraryStore((s) => s.selectSize);

  const isExpanded = selectedModelId === model.id;

  return (
    <div style={{ marginBottom: 10 }}>
      <button
        onClick={() => selectModel(isExpanded ? null : model.id)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          maxWidth: 480,
          padding: '10px 14px',
          background: 'var(--bg-panel-raised)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 8,
          color: 'var(--text-primary)',
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        <span>{model.name}</span>
        <span style={{ color: 'var(--text-tertiary)', fontSize: 11.5 }}>
          {model.sizes.length} size{model.sizes.length === 1 ? '' : 's'}
        </span>
      </button>

      {isExpanded && (
        <div style={{ marginTop: 8, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {model.sizes.map((size) => (
            <button
              key={size.id}
              onClick={() => selectSize(model.id, size.id)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px 12px',
                background: selectedSizeId === size.id ? 'var(--accent-wash)' : 'var(--bg-input)',
                border: `1px solid ${selectedSizeId === size.id ? 'var(--accent)' : 'var(--border-subtle)'}`,
                borderRadius: 6,
                color: 'var(--text-primary)',
                cursor: 'pointer',
                fontSize: 12,
                maxWidth: 464,
              }}
            >
              <span>Size {size.size_label}</span>
              <span style={{ color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
                {size.length_mm ? `${size.length_mm.toFixed(0)}mm` : '—'}
              </span>
            </button>
          ))}
          {selectedSizeId && model.sizes.some((s) => s.id === selectedSizeId) && (
            <SizeFilesList model={model} />
          )}
        </div>
      )}
    </div>
  );
}

export function LibraryPanel() {
  const models = useLibraryStore((s) => s.models);
  const isLoading = useLibraryStore((s) => s.isLoading);
  const loadError = useLibraryStore((s) => s.loadError);
  const loadLibrary = useLibraryStore((s) => s.loadLibrary);

  useEffect(() => {
    void loadLibrary();
  }, [loadLibrary]);

  return (
    <div className="panel-scroll">
      <BatchImportZone />

      <h3 style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10 }}>
        Saved Models
      </h3>

      {isLoading && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Loading library…</div>}

      {loadError && (
        <div
          style={{
            maxWidth: 520,
            padding: '10px 14px',
            background: 'var(--danger-wash)',
            border: '1px solid var(--danger)',
            borderRadius: 8,
            fontSize: 12,
            color: 'var(--text-primary)',
          }}
        >
          Couldn't load the library: {loadError}. If this is the first time using it, the backend may not be set up
          yet — see SETUP.md in the project.
        </div>
      )}

      {!isLoading && !loadError && models.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
          Nothing saved yet — drop files above, or use "Save to Library" in the Studio sidebar after loading a model.
        </div>
      )}

      {models.map((model) => (
        <ModelRow key={model.id} model={model} />
      ))}
    </div>
  );
}
